import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { 
  UtensilsCrossed, Plus, Minus, Bell, Receipt, Coffee,
  ChevronRight, Clock, Users, Check, AlertCircle, 
  ShoppingBag, RefreshCw, MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Public API instance (no auth token required)
const publicApi = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

const REQUEST_TYPES = [
  { id: 'assistance', icon: Bell, label: 'Need Assistance', color: 'bg-blue-100 text-blue-700' },
  { id: 'refill', icon: Coffee, label: 'Refill Drinks', color: 'bg-amber-100 text-amber-700' },
  { id: 'bill', icon: Receipt, label: 'Request Bill', color: 'bg-green-100 text-green-700' },
  { id: 'other', icon: MessageSquare, label: 'Other Request', color: 'bg-purple-100 text-purple-700' },
];

export default function TableOrderingPage() {
  const { id: restaurantId } = useParams();
  const [searchParams] = useSearchParams();
  const tableId = searchParams.get('table'); // Now expects table_id instead of table number
  const navigate = useNavigate();
  
  const [restaurant, setRestaurant] = useState(null);
  const [table, setTable] = useState(null);
  const [session, setSession] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [selectedRequestType, setSelectedRequestType] = useState(null);
  const [addingItem, setAddingItem] = useState(null);
  const [showStartSession, setShowStartSession] = useState(false);
  const [partySize, setPartySize] = useState(2);
  const [customerName, setCustomerName] = useState('');
  const [confirmingItem, setConfirmingItem] = useState(null);

  // Fetch data (no auth required)
  const fetchData = useCallback(async () => {
    try {
      // Get restaurant
      const restResponse = await publicApi.get(`/restaurants/${restaurantId}`);
      setRestaurant(restResponse.data);
      
      // Get menu items
      const menuResponse = await publicApi.get(`/restaurants/${restaurantId}/menu`);
      setMenuItems(menuResponse.data);
      
      // Extract unique categories
      const cats = ['all', ...new Set(menuResponse.data.map(item => item.category).filter(Boolean))];
      setCategories(cats);
      
      // If we have table ID, get table info and session
      if (tableId) {
        await fetchTableAndSession();
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load restaurant');
    } finally {
      setLoading(false);
    }
  }, [restaurantId, tableId]);

  const fetchTableAndSession = async () => {
    if (!tableId) return;
    
    try {
      // Get table info and any existing session
      const response = await publicApi.get(`/guest/table/${tableId}`);
      setTable(response.data.table);
      setRestaurant(response.data.restaurant);
      
      if (response.data.session) {
        setSession(response.data.session);
      } else {
        // No active session - prompt to start one
        setShowStartSession(true);
      }
    } catch (error) {
      console.error('Failed to get table info:', error);
      toast.error('Invalid table QR code');
    }
  };

  const fetchSession = async () => {
    if (!session?.id) return;
    
    try {
      const response = await publicApi.get(`/guest/session/${session.id}`);
      setSession(response.data);
    } catch (error) {
      console.log('Failed to refresh session');
    }
  };

  const startSession = async () => {
    if (!tableId) return;
    
    try {
      const response = await publicApi.post(`/guest/table/${tableId}/start-session`, {
        party_size: partySize,
        customer_name: customerName || 'Guest'
      });
      
      // Fetch the session we just started
      const sessionResponse = await publicApi.get(`/guest/session/${response.data.session_id}`);
      setSession(sessionResponse.data);
      setShowStartSession(false);
      toast.success('Session started! You can now order.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start session');
    }
  };

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchSession, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleAddItem = async (menuItem) => {
    if (!session) {
      setShowStartSession(true);
      return;
    }
    
    // Show confirmation dialog
    setConfirmingItem(menuItem);
  };

  const confirmAddItem = async () => {
    if (!confirmingItem || !session) return;
    
    setAddingItem(confirmingItem.id);
    try {
      const response = await publicApi.post(`/guest/session/${session.id}/items`, {
        menu_item_id: confirmingItem.id,
        quantity: 1,
        notes: '',
        added_by: 'guest',
        requires_confirmation: false // Already confirmed by user
      });
      
      toast.success(`${confirmingItem.name} added to order`);
      setConfirmingItem(null);
      fetchSession();
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setAddingItem(null);
    }
  };

  const handleRequestWaiter = async () => {
    if (!session) {
      toast.error('No active table session');
      return;
    }
    
    if (!selectedRequestType) {
      toast.error('Please select a request type');
      return;
    }
    
    try {
      await publicApi.post(`/guest/session/${session.id}/request-waiter`, {
        request_type: selectedRequestType,
        message: requestMessage
      });
      toast.success('Waiter has been notified');
      setShowRequestDialog(false);
      setSelectedRequestType(null);
      setRequestMessage('');
      fetchSession();
    } catch (error) {
      toast.error('Failed to request waiter');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount || 0);
  };

  const filteredMenu = menuItems.filter(item => 
    selectedCategory === 'all' || item.category === selectedCategory
  );

  const pendingRequests = session?.waiter_requests?.filter(r => r.status === 'pending').length || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold mb-2">Restaurant Not Found</h2>
            <Button onClick={() => navigate('/restaurants')}>Browse Restaurants</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pt-16 pb-32" data-testid="table-ordering-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-900">{restaurant.name}</h1>
              {session && (
                <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <UtensilsCrossed className="w-4 h-4" />
                    Table {session.table_number}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {session.party_size} guests
                  </span>
                </div>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchSession}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* No Session Warning - with button to start session */}
      {!session && tableId && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-800">Welcome to {table?.table_number ? `Table ${table.table_number}` : 'your table'}!</p>
                    <p className="text-sm text-amber-600">Start a session to begin ordering.</p>
                  </div>
                </div>
                <Button onClick={() => setShowStartSession(true)} className="btn-primary" data-testid="start-session-btn">
                  Start Ordering
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* No Table ID Warning */}
      {!tableId && (
        <div className="max-w-4xl mx-auto px-4 mt-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-800">Browse Menu</p>
                  <p className="text-sm text-blue-600">Scan the QR code at your table to order.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 mt-6">
        <Tabs defaultValue="menu" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="menu" data-testid="menu-tab">
              <UtensilsCrossed className="w-4 h-4 mr-2" />
              Menu
            </TabsTrigger>
            <TabsTrigger value="order" data-testid="order-tab">
              <ShoppingBag className="w-4 h-4 mr-2" />
              My Order ({session?.items?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Menu Tab */}
          <TabsContent value="menu" className="space-y-4">
            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'btn-primary' : ''}
                >
                  {cat === 'all' ? 'All' : cat}
                </Button>
              ))}
            </div>

            {/* Menu Items */}
            <div className="grid gap-3">
              {filteredMenu.map(item => (
                <Card key={item.id} className="overflow-hidden" data-testid={`menu-item-${item.id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold text-slate-900">{item.name}</h3>
                            <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
                          </div>
                          <p className="font-bold text-orange-600 whitespace-nowrap">
                            {formatCurrency(item.price)}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <Badge variant="secondary" className="text-xs">
                            {item.category}
                          </Badge>
                          <Button
                            size="sm"
                            onClick={() => handleAddItem(item)}
                            disabled={!session || addingItem === item.id}
                            className="btn-primary"
                            data-testid={`add-item-${item.id}`}
                          >
                            {addingItem === item.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Plus className="w-4 h-4 mr-1" /> Add
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Order Tab */}
          <TabsContent value="order" className="space-y-4">
            {!session?.items?.length ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No items yet</h3>
                  <p className="text-slate-500">Add items from the menu to start your order</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="space-y-3">
                  {session.items.map((item, idx) => (
                    <Card key={item.id || idx} data-testid={`order-item-${item.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-slate-900">{item.name}</h4>
                              <Badge 
                                className={
                                  item.status === 'served' ? 'bg-green-100 text-green-700' :
                                  item.status === 'preparing' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-700'
                                }
                              >
                                {item.status === 'served' ? 'Served' : 
                                 item.status === 'preparing' ? 'Preparing' : 'Pending'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">
                              x{item.quantity} · Added by {item.added_by}
                            </p>
                            {item.notes && (
                              <p className="text-sm text-slate-400 mt-1">Note: {item.notes}</p>
                            )}
                          </div>
                          <p className="font-bold text-slate-900">{formatCurrency(item.total)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Order Total */}
                <Card className="bg-slate-900 text-white">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between text-lg">
                      <span>Subtotal</span>
                      <span className="font-bold">{formatCurrency(session.subtotal)}</span>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Pending Requests */}
            {pendingRequests > 0 && (
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-blue-600 animate-pulse" />
                    <div>
                      <p className="font-medium text-blue-800">Waiter Notified</p>
                      <p className="text-sm text-blue-600">{pendingRequests} pending request(s)</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Fixed Bottom Bar */}
      {session && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-30">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-slate-500">{session.items?.length || 0} items</p>
              <p className="text-xl font-bold text-slate-900">{formatCurrency(session.subtotal)}</p>
            </div>
            <Button 
              onClick={() => setShowRequestDialog(true)}
              className="btn-primary flex-1 max-w-xs"
              data-testid="request-waiter-btn"
            >
              <Bell className="w-5 h-5 mr-2" />
              Call Waiter
            </Button>
          </div>
        </div>
      )}

      {/* Request Waiter Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Assistance</DialogTitle>
            <DialogDescription>
              Select what you need and a waiter will be notified
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              {REQUEST_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedRequestType(type.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      selectedRequestType === type.id 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`request-type-${type.id}`}
                  >
                    <Icon className={`w-6 h-6 mx-auto mb-2 ${
                      selectedRequestType === type.id ? 'text-orange-500' : 'text-slate-400'
                    }`} />
                    <p className={`text-sm font-medium ${
                      selectedRequestType === type.id ? 'text-orange-700' : 'text-slate-700'
                    }`}>
                      {type.label}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Additional Message (optional)</label>
              <Textarea
                placeholder="Any specific details..."
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                rows={2}
                data-testid="request-message"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestWaiter} 
              className="btn-primary"
              disabled={!selectedRequestType}
              data-testid="send-request-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Send Request
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Start Session Dialog */}
      <Dialog open={showStartSession} onOpenChange={setShowStartSession}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start Your Table Session</DialogTitle>
            <DialogDescription>
              {table ? `Table ${table.table_number}` : 'Enter your details to start ordering'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Your Name (optional)</label>
              <Input
                placeholder="Enter your name"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                data-testid="customer-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Party Size</label>
              <div className="flex items-center gap-4">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setPartySize(Math.max(1, partySize - 1))}
                  data-testid="party-size-minus"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="text-2xl font-bold w-12 text-center">{partySize}</span>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setPartySize(Math.min(20, partySize + 1))}
                  data-testid="party-size-plus"
                >
                  <Plus className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-500">guests</span>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowStartSession(false)}>
              Cancel
            </Button>
            <Button 
              onClick={startSession} 
              className="btn-primary"
              data-testid="confirm-start-session-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Start Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Confirmation Dialog */}
      <Dialog open={!!confirmingItem} onOpenChange={() => setConfirmingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Order?</DialogTitle>
            <DialogDescription>
              Confirm you want to add this item to your order
            </DialogDescription>
          </DialogHeader>
          
          {confirmingItem && (
            <div className="py-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                {confirmingItem.image_url && (
                  <img 
                    src={confirmingItem.image_url} 
                    alt={confirmingItem.name}
                    className="w-16 h-16 object-cover rounded-lg"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900">{confirmingItem.name}</h3>
                  <p className="text-sm text-slate-500">{confirmingItem.description}</p>
                </div>
                <p className="font-bold text-orange-600 text-lg">
                  {formatCurrency(confirmingItem.price)}
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setConfirmingItem(null)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddItem} 
              className="btn-primary"
              disabled={addingItem}
              data-testid="confirm-add-item-btn"
            >
              {addingItem ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Add to Order
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
